import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Heart, PartyPopper, Sparkles, Flower2, Crown, GemIcon, Star, Calendar, Gift, MapPin, LucideIcon } from 'lucide-react';
import { MenuItemConfig } from '@/components/navbar/menuConfig';

interface VisualConfig {
  icon: LucideIcon;
  accentColor: string;
  bgGradient: string;
  fallbackTagline: string;
}

const getVisualConfig = (slug: string): VisualConfig => {
  const configs: Record<string, VisualConfig> = {
    'wedding-planning-paris': {
      icon: Crown,
      accentColor: '#C9A227',
      bgGradient: 'from-[#C9A227]/5 to-transparent',
      fallbackTagline: 'Votre jour inoubliable'
    },
    'wedding-design-scenographie': {
      icon: Sparkles,
      accentColor: '#C9A227',
      bgGradient: 'from-[#C9A227]/5 to-transparent',
      fallbackTagline: 'Décoration et mise en scène sur-mesure'
    },
    'anniversaire-signature': {
      icon: PartyPopper,
      accentColor: 'hsl(var(--primary))',
      bgGradient: 'from-[#1E3A5F]/5 to-transparent',
      fallbackTagline: 'Fêtez vos années avec style'
    },
    'demande-mariage-paris': {
      icon: GemIcon,
      accentColor: 'hsl(var(--primary))',
      bgGradient: 'from-[#F28C8C]/5 to-transparent',
      fallbackTagline: 'Un moment unique et romantique'
    },
  };

  return configs[slug] || {
    icon: Calendar,
    accentColor: 'hsl(var(--primary))',
    bgGradient: 'from-[#1E3A5F]/5 to-transparent',
    fallbackTagline: 'Évènement privé sur mesure'
  };
};

export interface PrivateMenuItem extends MenuItemConfig {
  id: string;
}

export const usePrivateMenuItems = () => {
  return useQuery({
    queryKey: ['private-menu-items'],
    queryFn: async (): Promise<PrivateMenuItem[]> => {
      const { data, error } = await supabase
        .from('event_categories')
        .select('id, title, slug, description')
        .eq('type_categorie', 'particulier')
        .eq('is_active', true)
        .order('order');

      if (error) {
        console.error('Error fetching private menu items:', error);
        throw error;
      }

      const items: PrivateMenuItem[] = (data || []).map((category) => {
        const visualConfig = getVisualConfig(category.slug || '');

        return {
          id: category.id,
          title: category.title,
          path: `/univers-particuliers/${category.slug}`,
          icon: visualConfig.icon,
          tagline: category.description?.split('.')[0] || visualConfig.fallbackTagline,
          accentColor: visualConfig.accentColor,
          bgGradient: visualConfig.bgGradient
        };
      });

      // Ajouter les items statiques à la fin
      items.push({
        id: 'nos-offres-static',
        title: 'Nos offres privées',
        path: '/nos-offres',
        icon: Gift,
        tagline: 'Des formules sur-mesure pour vous',
        accentColor: '#C9A227',
        bgGradient: 'from-[#C9A227]/5 to-transparent'
      });

      items.push({
        id: 'lieux-partenaires-static',
        title: 'Nos lieux partenaires',
        path: '/lieux',
        icon: MapPin,
        tagline: 'Châteaux, rooftops, lofts… sélectionnés pour vous',
        accentColor: 'hsl(var(--primary))',
        bgGradient: 'from-[#1E3A5F]/5 to-transparent'
      });

      return items;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000
  });
};
